// First Created - 07 Oct 2026
// Author - Atul Pandey
// Purpose - Admin list page for Org Trail Session Q&A Assignments


import React, {
    useMemo,
    useState,
} from "react"


import useTitle
    from "@/hooks/useTitle"


import {
    addIcon,
} from "@/assets/img/imgAssets"


import {
    PlusIcon,
    MinusIcon,
    TrashIcon,
    PencilSquareIcon,
} from "@heroicons/react/24/outline"


import {
    BuildingOfficeIcon,
    AcademicCapIcon,
    UserGroupIcon,
    RectangleStackIcon,
    ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/solid"


import PulseLoader
    from "react-spinners/PulseLoader"


import {
    useGetOrgTrailSessionQNAAssignmentsQuery,
} from "./orgTrailSessionQNAAssignmentsApiSlice"



const getRefId = (
    value
) => {

    if (
        !value
    ) {
        return "not-defined"
    }


    if (
        typeof value ===
        "string"
    ) {
        return value
    }


    return (
        value?._id ||
        value?.id ||
        "not-defined"
    )
}



const EtOrgTrailSessionQNAAssignmentList = ({
    onAdd,
    onUpdate,
    onDelete,
}) => {

    useTitle(
        "Session Q&A Assignments"
    )


    const [
        showOrgGroups,
        setShowOrgGroups,
    ] =
        useState({})


    const [
        showTrailGroups,
        setShowTrailGroups,
    ] =
        useState({})


    const [
        showCohortGroups,
        setShowCohortGroups,
    ] =
        useState({})


    const [
        showSessionGroups,
        setShowSessionGroups,
    ] =
        useState({})



    const {
        data:
            assignmentsData,

        isError,
        error,
        isLoading,
    } =
        useGetOrgTrailSessionQNAAssignmentsQuery()



    const assignments =
        useMemo(
            () => {

                return (
                    assignmentsData
                        ?.ids
                        ?.map(
                            id =>
                                assignmentsData
                                    ?.entities[
                                        id
                                    ]
                        )
                        .filter(
                            Boolean
                        ) ||
                    []
                )

            },
            [
                assignmentsData,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | Group Assignments
    |--------------------------------------------------------------------------
    */

    const groupedAssignments =
        useMemo(
            () => {

                return assignments.reduce(
                    (
                        acc,
                        assignment
                    ) => {

                        const orgId =
                            getRefId(
                                assignment
                                    ?.orgId
                            )


                        const trailId =
                            getRefId(
                                assignment
                                    ?.trailId
                            )


                        const cohortId =
                            getRefId(
                                assignment
                                    ?.cohortId
                            )


                        const sessionId =
                            getRefId(
                                assignment
                                    ?.sessionId
                            )



                        if (
                            !acc[
                                orgId
                            ]
                        ) {

                            acc[
                                orgId
                            ] = {

                                orgId,

                                orgInfo:
                                    assignment
                                        ?.orgId,

                                assignmentCount:
                                    0,

                                trails:
                                    {},

                            }
                        }



                        if (
                            !acc[
                                orgId
                            ]
                                .trails[
                                    trailId
                                ]
                        ) {

                            acc[
                                orgId
                            ]
                                .trails[
                                    trailId
                                ] = {

                                trailId,

                                trailInfo:
                                    assignment
                                        ?.trailId,

                                assignmentCount:
                                    0,

                                cohorts:
                                    {},

                            }
                        }



                        if (
                            !acc[
                                orgId
                            ]
                                .trails[
                                    trailId
                                ]
                                .cohorts[
                                    cohortId
                                ]
                        ) {

                            acc[
                                orgId
                            ]
                                .trails[
                                    trailId
                                ]
                                .cohorts[
                                    cohortId
                                ] = {

                                cohortId,

                                cohortInfo:
                                    assignment
                                        ?.cohortId,

                                assignmentCount:
                                    0,

                                sessions:
                                    {},

                            }
                        }



                        if (
                            !acc[
                                orgId
                            ]
                                .trails[
                                    trailId
                                ]
                                .cohorts[
                                    cohortId
                                ]
                                .sessions[
                                    sessionId
                                ]
                        ) {

                            acc[
                                orgId
                            ]
                                .trails[
                                    trailId
                                ]
                                .cohorts[
                                    cohortId
                                ]
                                .sessions[
                                    sessionId
                                ] = {

                                sessionId,

                                sessionInfo:
                                    assignment
                                        ?.sessionId,

                                assignmentCount:
                                    0,

                                assignments:
                                    [],

                            }
                        }



                        acc[
                            orgId
                        ]
                            .trails[
                                trailId
                            ]
                            .cohorts[
                                cohortId
                            ]
                            .sessions[
                                sessionId
                            ]
                            .assignments
                            .push(
                                assignment
                            )



                        acc[
                            orgId
                        ]
                            .assignmentCount +=
                            1


                        acc[
                            orgId
                        ]
                            .trails[
                                trailId
                            ]
                            .assignmentCount +=
                            1


                        acc[
                            orgId
                        ]
                            .trails[
                                trailId
                            ]
                            .cohorts[
                                cohortId
                            ]
                            .assignmentCount +=
                            1


                        acc[
                            orgId
                        ]
                            .trails[
                                trailId
                            ]
                            .cohorts[
                                cohortId
                            ]
                            .sessions[
                                sessionId
                            ]
                            .assignmentCount +=
                            1


                        return acc

                    },
                    {}
                )

            },
            [
                assignments,
            ]
        )



    const orgGroups =
        useMemo(
            () => {

                return Object
                    .values(
                        groupedAssignments
                    )
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            (
                                a
                                    ?.orgInfo
                                    ?.organizationName ||
                                ""
                            )
                                .localeCompare(
                                    b
                                        ?.orgInfo
                                        ?.organizationName ||
                                    ""
                                )
                    )

            },
            [
                groupedAssignments,
            ]
        )



    if (
        isLoading
    ) {

        return (
            <PulseLoader
                color="#ea580c"
            />
        )
    }



    if (
        isError
    ) {

        return (

            <div
                className="text-sm text-orange-700"
            >
                Error occurred:{" "}
                {
                    error
                        ?.data
                        ?.message ||
                    "Unable to load Session Q&A assignments."
                }
            </div>
        )
    }



    return (

        <div
            className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto"
        >

            {/* Header */}

            <div
                className="flex justify-between items-center m-1 p-1"
            >

                <div
                    className="flex justify-start items-center"
                >

                    <p
                        className="text-2xl m-1 p-1 font-bold"
                    >
                        Session Q&A Assignments
                    </p>


                    <img
                        src={
                            addIcon
                        }

                        alt=""

                        className="h-10 w-10 cursor-pointer"

                        onClick={
                            onAdd
                        }
                    />

                </div>


                <div
                    className="flex justify-start items-center mx-2"
                >

                    <p
                        className="text-[10px] text-gray-500 mx-1"
                    >
                        Total:
                    </p>


                    <div
                        className="rounded-full flex justify-center items-center h-6 w-6 border border-orange-600 bg-white"
                    >
                        <p
                            className="text-orange-600 text-[10px] font-bold"
                        >
                            {
                                assignments
                                    .length
                            }
                        </p>
                    </div>

                </div>

            </div>



            {
                assignments
                    .length ===
                    0 &&
                (
                    <div
                        className="bg-white rounded m-1 p-4"
                    >
                        <p
                            className="text-sm text-gray-600"
                        >
                            No Session Q&A assignments found.
                            Click the add icon to configure cohort visibility.
                        </p>
                    </div>
                )
            }



            {
                orgGroups.map(
                    (
                        orgGroup,
                        orgIndex
                    ) => {

                        const orgId =
                            orgGroup
                                .orgId


                        const orgName =
                            orgGroup
                                ?.orgInfo
                                ?.organizationName ||
                            "Organization not defined"


                        const trails =
                            Object.values(
                                orgGroup
                                    .trails
                            )


                        return (

                            <div
                                key={
                                    orgId
                                }

                                className="m-1 p-1 bg-white rounded"
                            >

                                {/* Organization */}

                                <div
                                    className="bg-gray-100 rounded m-1 py-3 p-2 flex justify-between items-center"
                                >

                                    <div
                                        className="flex justify-start items-center"
                                    >

                                        <button
                                            type="button"

                                            onClick={
                                                () =>
                                                    setShowOrgGroups(
                                                        prev => ({
                                                            ...prev,

                                                            [orgId]:
                                                                !prev[
                                                                    orgId
                                                                ],
                                                        })
                                                    )
                                            }

                                            className="m-1 p-1 rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                                        >
                                            {
                                                showOrgGroups[
                                                    orgId
                                                ]
                                                    ?
                                                    <MinusIcon
                                                        className="h-5 w-5 text-orange-600"
                                                    />
                                                    :
                                                    <PlusIcon
                                                        className="h-5 w-5 text-orange-600"
                                                    />
                                            }
                                        </button>


                                        <BuildingOfficeIcon
                                            className="h-5 w-5 text-orange-600 mx-2"
                                        />


                                        <p
                                            className="text-sm font-bold"
                                        >
                                            {
                                                orgIndex +
                                                1
                                            }

                                            {" - "}

                                            {
                                                orgName
                                            }
                                        </p>

                                    </div>


                                    <p
                                        className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-3 py-1"
                                    >
                                        Assignments:{" "}
                                        {
                                            orgGroup
                                                .assignmentCount
                                        }
                                    </p>

                                </div>



                                {
                                    showOrgGroups[
                                        orgId
                                    ] &&
                                    trails.map(
                                        (
                                            trailGroup,
                                            trailIndex
                                        ) => {

                                            const trailId =
                                                trailGroup
                                                    .trailId


                                            const trailKey =
                                                `${orgId}-${trailId}`


                                            const trailName =
                                                trailGroup
                                                    ?.trailInfo
                                                    ?.trailName ||
                                                "Trail not defined"


                                            const cohorts =
                                                Object.values(
                                                    trailGroup
                                                        .cohorts
                                                )


                                            return (

                                                <div
                                                    key={
                                                        trailKey
                                                    }

                                                    className="bg-gray-100 rounded m-1 p-1"
                                                >

                                                    {/* Trail */}

                                                    <div
                                                        className="bg-white rounded m-1 p-2 flex justify-between items-center"
                                                    >

                                                        <div
                                                            className="flex justify-start items-center"
                                                        >

                                                            <button
                                                                type="button"

                                                                onClick={
                                                                    () =>
                                                                        setShowTrailGroups(
                                                                            prev => ({
                                                                                ...prev,

                                                                                [trailKey]:
                                                                                    !prev[
                                                                                        trailKey
                                                                                    ],
                                                                            })
                                                                        )
                                                                }

                                                                className="m-1 p-1 rounded-full h-7 w-7 flex justify-center items-center hover:bg-gray-100"
                                                            >
                                                                {
                                                                    showTrailGroups[
                                                                        trailKey
                                                                    ]
                                                                        ?
                                                                        <MinusIcon
                                                                            className="h-5 w-5 text-orange-600"
                                                                        />
                                                                        :
                                                                        <PlusIcon
                                                                            className="h-5 w-5 text-orange-600"
                                                                        />
                                                                }
                                                            </button>


                                                            <AcademicCapIcon
                                                                className="h-5 w-5 text-orange-600 mx-2"
                                                            />


                                                            <p
                                                                className="text-xs font-bold"
                                                            >
                                                                {
                                                                    trailIndex +
                                                                    1
                                                                }

                                                                {" - "}

                                                                {
                                                                    trailName
                                                                }
                                                            </p>

                                                        </div>


                                                        <p
                                                            className="text-[9px] bg-lime-100 rounded-full px-2 py-1"
                                                        >
                                                            Assignments:{" "}
                                                            {
                                                                trailGroup
                                                                    .assignmentCount
                                                            }
                                                        </p>

                                                    </div>



                                                    {
                                                        showTrailGroups[
                                                            trailKey
                                                        ] &&
                                                        cohorts.map(
                                                            cohortGroup => {

                                                                const cohortId =
                                                                    cohortGroup
                                                                        .cohortId


                                                                const cohortKey =
                                                                    `${orgId}-${trailId}-${cohortId}`


                                                                const cohortName =
                                                                    cohortGroup
                                                                        ?.cohortInfo
                                                                        ?.cohortName ||
                                                                    cohortGroup
                                                                        ?.cohortInfo
                                                                        ?.cohortCode ||
                                                                    "Cohort not defined"


                                                                const sessions =
                                                                    Object.values(
                                                                        cohortGroup
                                                                            .sessions
                                                                    )


                                                                return (

                                                                    <div
                                                                        key={
                                                                            cohortKey
                                                                        }

                                                                        className="bg-white rounded m-1 p-1"
                                                                    >

                                                                        {/* Cohort */}

                                                                        <div
                                                                            className="bg-gray-100 rounded m-1 p-2 flex justify-between items-center"
                                                                        >

                                                                            <div
                                                                                className="flex justify-start items-center"
                                                                            >

                                                                                <button
                                                                                    type="button"

                                                                                    onClick={
                                                                                        () =>
                                                                                            setShowCohortGroups(
                                                                                                prev => ({
                                                                                                    ...prev,

                                                                                                    [cohortKey]:
                                                                                                        !prev[
                                                                                                            cohortKey
                                                                                                        ],
                                                                                                })
                                                                                            )
                                                                                    }

                                                                                    className="m-1 p-1 rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                                                                                >
                                                                                    {
                                                                                        showCohortGroups[
                                                                                            cohortKey
                                                                                        ]
                                                                                            ?
                                                                                            <MinusIcon
                                                                                                className="h-5 w-5 text-orange-600"
                                                                                            />
                                                                                            :
                                                                                            <PlusIcon
                                                                                                className="h-5 w-5 text-orange-600"
                                                                                            />
                                                                                    }
                                                                                </button>


                                                                                <UserGroupIcon
                                                                                    className="h-4 w-4 text-orange-600 mx-2"
                                                                                />


                                                                                <p
                                                                                    className="text-xs font-bold"
                                                                                >
                                                                                    {
                                                                                        cohortName
                                                                                    }
                                                                                </p>

                                                                            </div>


                                                                            <p
                                                                                className="text-[9px] bg-orange-100 text-orange-700 rounded-full px-2 py-1"
                                                                            >
                                                                                Assignments:{" "}
                                                                                {
                                                                                    cohortGroup
                                                                                        .assignmentCount
                                                                                }
                                                                            </p>

                                                                        </div>



                                                                        {
                                                                            showCohortGroups[
                                                                                cohortKey
                                                                            ] &&
                                                                            sessions.map(
                                                                                sessionGroup => {

                                                                                    const sessionId =
                                                                                        sessionGroup
                                                                                            .sessionId


                                                                                    const sessionKey =
                                                                                        `${cohortKey}-${sessionId}`


                                                                                    const sessionName =
                                                                                        sessionGroup
                                                                                            ?.sessionInfo
                                                                                            ?.sessionName ||
                                                                                        "Session not defined"


                                                                                    const sessionAssignments =
                                                                                        sessionGroup
                                                                                            .assignments
                                                                                            .slice()
                                                                                            .sort(
                                                                                                (
                                                                                                    a,
                                                                                                    b
                                                                                                ) =>
                                                                                                    (
                                                                                                        a
                                                                                                            ?.sessionQNASetId
                                                                                                            ?.sessionQNAName ||
                                                                                                        ""
                                                                                                    )
                                                                                                        .localeCompare(
                                                                                                            b
                                                                                                                ?.sessionQNASetId
                                                                                                                ?.sessionQNAName ||
                                                                                                            ""
                                                                                                        )
                                                                                            )


                                                                                    return (

                                                                                        <div
                                                                                            key={
                                                                                                sessionKey
                                                                                            }

                                                                                            className="bg-gray-100 rounded m-1 p-1"
                                                                                        >

                                                                                            {/* Session */}

                                                                                            <div
                                                                                                className="bg-white rounded m-1 p-2 flex justify-between items-center"
                                                                                            >

                                                                                                <div
                                                                                                    className="flex justify-start items-center"
                                                                                                >

                                                                                                    <button
                                                                                                        type="button"

                                                                                                        onClick={
                                                                                                            () =>
                                                                                                                setShowSessionGroups(
                                                                                                                    prev => ({
                                                                                                                        ...prev,

                                                                                                                        [sessionKey]:
                                                                                                                            !prev[
                                                                                                                                sessionKey
                                                                                                                            ],
                                                                                                                    })
                                                                                                                )
                                                                                                        }

                                                                                                        className="m-1 p-1 rounded-full h-7 w-7 flex justify-center items-center hover:bg-gray-100"
                                                                                                    >
                                                                                                        {
                                                                                                            showSessionGroups[
                                                                                                                sessionKey
                                                                                                            ]
                                                                                                                ?
                                                                                                                <MinusIcon
                                                                                                                    className="h-5 w-5 text-orange-600"
                                                                                                                />
                                                                                                                :
                                                                                                                <PlusIcon
                                                                                                                    className="h-5 w-5 text-orange-600"
                                                                                                                />
                                                                                                        }
                                                                                                    </button>


                                                                                                    <RectangleStackIcon
                                                                                                        className="h-4 w-4 text-orange-600 mx-2"
                                                                                                    />


                                                                                                    <p
                                                                                                        className="text-xs font-bold"
                                                                                                    >
                                                                                                        {
                                                                                                            sessionName
                                                                                                        }
                                                                                                    </p>

                                                                                                </div>


                                                                                                <p
                                                                                                    className="text-[9px] bg-lime-100 rounded-full px-2 py-1"
                                                                                                >
                                                                                                    Q&A Sets:{" "}
                                                                                                    {
                                                                                                        sessionGroup
                                                                                                            .assignmentCount
                                                                                                    }
                                                                                                </p>

                                                                                            </div>



                                                                                            {
                                                                                                showSessionGroups[
                                                                                                    sessionKey
                                                                                                ] &&
                                                                                                (
                                                                                                    <div
                                                                                                        className="bg-white rounded m-1 p-1"
                                                                                                    >

                                                                                                        {
                                                                                                            sessionAssignments.map(
                                                                                                                (
                                                                                                                    assignment,
                                                                                                                    index
                                                                                                                ) => (

                                                                                                                    <div
                                                                                                                        key={
                                                                                                                            assignment
                                                                                                                                ?._id
                                                                                                                        }

                                                                                                                        className="bg-gray-100 rounded m-1 p-2 flex justify-between items-center"
                                                                                                                    >

                                                                                                                        <div
                                                                                                                            className="flex justify-start items-center"
                                                                                                                        >

                                                                                                                            <ChatBubbleLeftRightIcon
                                                                                                                                className="h-4 w-4 text-orange-600 mx-2"
                                                                                                                            />


                                                                                                                            <p
                                                                                                                                className="text-xs font-bold"
                                                                                                                            >
                                                                                                                                {
                                                                                                                                    index +
                                                                                                                                    1
                                                                                                                                }

                                                                                                                                {" - "}

                                                                                                                                {
                                                                                                                                    assignment
                                                                                                                                        ?.sessionQNASetId
                                                                                                                                        ?.sessionQNAName ||
                                                                                                                                    "Q&A Set not defined"
                                                                                                                                }
                                                                                                                            </p>


                                                                                                                            <p
                                                                                                                                className={`text-[9px] rounded-full mx-2 px-2 py-1 ${
                                                                                                                                    assignment
                                                                                                                                        ?.active !==
                                                                                                                                        false
                                                                                                                                        ?
                                                                                                                                        "bg-lime-100 text-gray-700"
                                                                                                                                        :
                                                                                                                                        "bg-red-100 text-red-700"
                                                                                                                                }`}
                                                                                                                            >
                                                                                                                                {
                                                                                                                                    assignment
                                                                                                                                        ?.active !==
                                                                                                                                        false
                                                                                                                                        ?
                                                                                                                                        "Active"
                                                                                                                                        :
                                                                                                                                        "Inactive"
                                                                                                                                }
                                                                                                                            </p>

                                                                                                                        </div>



                                                                                                                        <div
                                                                                                                            className="flex justify-start items-center"
                                                                                                                        >

                                                                                                                            <PencilSquareIcon
                                                                                                                                onClick={
                                                                                                                                    () =>
                                                                                                                                        onUpdate(
                                                                                                                                            assignment
                                                                                                                                                ?._id
                                                                                                                                        )
                                                                                                                                }

                                                                                                                                className="h-4 w-4 text-orange-600 cursor-pointer mx-2"
                                                                                                                            />


                                                                                                                            <TrashIcon
                                                                                                                                onClick={
                                                                                                                                    () =>
                                                                                                                                        onDelete(
                                                                                                                                            assignment
                                                                                                                                                ?._id
                                                                                                                                        )
                                                                                                                                }

                                                                                                                                className="h-4 w-4 text-orange-600 cursor-pointer mx-2"
                                                                                                                            />

                                                                                                                        </div>

                                                                                                                    </div>

                                                                                                                )
                                                                                                            )
                                                                                                        }

                                                                                                    </div>
                                                                                                )
                                                                                            }

                                                                                        </div>
                                                                                    )
                                                                                }
                                                                            )
                                                                        }

                                                                    </div>
                                                                )
                                                            }
                                                        )
                                                    }

                                                </div>
                                            )
                                        }
                                    )
                                }

                            </div>
                        )
                    }
                )
            }

        </div>
    )
}


export default EtOrgTrailSessionQNAAssignmentList